import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyName, SurveyNameDTO } from "../types/surveyName";
/* eslint-disable import/export */
export abstract class CloneSurveyRPC implements
  LeoRPC<CloneSurveyRPC.Request, CloneSurveyRPC.Response, CloneSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: CloneSurveyRPC.Request
  ): Promise<LeoRPCResult<CloneSurveyRPC.Response, CloneSurveyRPC.Errors.Errors>>;
}

export namespace CloneSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyName: SurveyNameDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCloneSurveyRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyName: SurveyName;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyName: SurveyName
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyName = surveyName;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyName = SurveyName.fromDTO(getObject(dto, "surveyName", InvalidRequestSurveyNameError));
      return new Request(
        projectId,
        surveyId,
        surveyName
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyName: this.surveyName.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CloneSurveyRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyName='${this.surveyName}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isCloneSurveyResponse: boolean = true;
  
    readonly surveyId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID
    ) {
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidResponseSurveyIdError);
      return new Response(
        surveyId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyId: this.surveyId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `CloneSurvey(surveyId='${this.surveyId}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    SurveyNameNotUnique = "SURVEY_NAME_NOT_UNIQUE",
    MaximumSurveyCountExceeded = "MAXIMUM_SURVEY_COUNT_EXCEEDED",
    InvalidSurveyName = "INVALID_SURVEY_NAME"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface SurveyNameNotUniqueDTO {
      code: string;
    }
    
    export class SurveyNameNotUnique extends LeoRPCError<RPCError> {
      readonly isSurveyNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNameNotUnique;
        super(code);
      }
    
    
      toDTO(): SurveyNameNotUniqueDTO {
        const dto: SurveyNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNameNotUnique.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.SURVEY_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface MaximumSurveyCountExceededDTO {
      code: string;
    }
    
    export class MaximumSurveyCountExceeded extends LeoRPCError<RPCError> {
      readonly isMaximumSurveyCountExceeded: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumSurveyCountExceeded;
        super(code);
      }
    
    
      toDTO(): MaximumSurveyCountExceededDTO {
        const dto: MaximumSurveyCountExceededDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumSurveyCountExceeded.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.MAXIMUM_SURVEY_COUNT_EXCEEDED()`;
    };
    
    
    export interface InvalidSurveyNameDTO {
      code: string;
    }
    
    export class InvalidSurveyName extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyName;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyNameDTO {
        const dto: InvalidSurveyNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyName.prototype.toString = function toString(): string {
      return `CloneSurveyRPC.INVALID_SURVEY_NAME()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSurveyId | SurveyNameNotUnique | MaximumSurveyCountExceeded | InvalidSurveyName;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyIdDTO | SurveyNameNotUniqueDTO | MaximumSurveyCountExceededDTO | InvalidSurveyNameDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.SurveyNameNotUnique:
          return new SurveyNameNotUnique();
        case RPCError.MaximumSurveyCountExceeded:
          return new MaximumSurveyCountExceeded();
        case RPCError.InvalidSurveyName:
          return new InvalidSurveyName();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends CloneSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends CloneSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyNameError extends CloneSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyNameError";
  }
}

export class InvalidResponseSurveyIdError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseSurveyNameNotUniqueError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNameNotUniqueError";
  }
}

export class InvalidResponseMaximumSurveyCountExceededError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumSurveyCountExceededError";
  }
}

export class InvalidResponseInvalidSurveyNameError extends CloneSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyNameError";
  }
}

